import jsPDF from "jspdf";
import uRLs from "../../utils/baseUtils";
import QRCode from "qrcode";
import autoTable from "jspdf-autotable";
import { loadImageDimensions, urlToBase64, capitalizeFirstLetter, parseAmount } from "../modules/invoiceUtils";
import { usePermission } from "../../context/PermissionContext";

export const generateIN_HA4_3PDF = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
   const {
    documentConfig,
    isPreSale,    
    isInterState,
    timezone
  } = invoiceConfig; 
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const centerX = pageW / 2;
  let y = PADDING; // Starting height
 
  const documentTitle = documentConfig?.title ||" ";
  const invLabels = documentConfig?.numberLabel;

  const imagePath = documentConfig?.image;

  try {
    // ---------- HEADER HEIGHT ----------
    let finalH = 12;

    // ---------- INVOICE DETAILS ----------
    const invLabel = invLabels || "";
    const invVal = documentConfig?.number || "-";
    const invDateTime = documentConfig?.Date?.time || "";

    const dateStr = `DATE: ${documentConfig?.Date?.date || ""}`;
    const timeStr = `TIME: ${invDateTime}`;

    // ---------- LOGO ----------
    if (imagePath) {
      const logoBase64 = await urlToBase64(imagePath);
      const { width, height } = await loadImageDimensions(logoBase64);

      const maxWidth = 30;
      const maxHeight = 12;

      const ratio = Math.min(
        maxWidth / width,
        maxHeight / height
      );

      const finalW = width * ratio;
      finalH = height * ratio;

      doc.addImage(
        logoBase64,
        "PNG",
        MARGIN_LEFT,
        y,
        finalW,
        finalH
      );
    }

    // ---------- DOCUMENT TITLE ----------
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100);

    doc.text(
      documentTitle || "",
      centerX,
      y + 3,
      { align: "center" }
    );

    // ---------- STORE NAME ----------
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0);

    doc.text(
      documentConfig?.fromData?.title?.toUpperCase() || "",
      centerX,
      y + 8,
      { align: "center" }
    );

    // ---------- INVOICE NUMBER ----------
    doc.setFontSize(7);
    doc.setFont("Inter-Regular", "normal");

    const invValW = doc.getTextWidth(invVal);
    const invLabelW = doc.getTextWidth(invLabel);

    doc.text(
      invLabel,
      MARGIN_RIGHT - invValW - invLabelW,
      y + 2
    );

    doc.setFont("Inter-Bold", "bold");

    doc.text(
      invVal,
      MARGIN_RIGHT,
      y + 2,
      { align: "right" }
    );

    // ---------- DATE / TIME LINE ----------
    doc.setFont("Inter-Regular", "normal");

    const dateWidth = doc.getTextWidth(dateStr);
    const timeWidth = doc.getTextWidth(timeStr);

    const totalDateTimeWidth = dateWidth + timeWidth;

    const lineStartX =
      MARGIN_RIGHT - totalDateTimeWidth - 1;

    doc.setDrawColor(
      selectedTheme?.templateTheme?.borderColor
    );

    doc.setLineWidth(0.1);

    doc.line(
      lineStartX,
      y + 3.5,
      MARGIN_RIGHT,
      y + 3.5
    );

    // Date
    doc.text(
      dateStr,
      MARGIN_RIGHT - timeWidth - 1,
      y + 6,
      { align: "right" }
    );

    // Time
    doc.text(
      timeStr,
      MARGIN_RIGHT,
      y + 6,
      { align: "right" }
    );

    // ---------- ADDRESS ----------
    // If image exists → address below logo.
    // If image doesn't exist → use the logo area.
    let addrY;

    if (imagePath) {
      addrY = y + finalH + 4;
    } else {
      addrY = y + 4;
    }

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    doc.setTextColor(60, 60, 60);

    let currentTextY = addrY;

    const addrLine1 = [
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
    ]
      .filter(Boolean)
      .join(", ");

    const addrLine2 = [
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      documentConfig?.fromData?.pincode,
    ]
      .filter(Boolean)
      .join(", ");

    // ---------- ADDRESS LINE 1 ----------
    if (addrLine1.trim()) {
      doc.text(
        addrLine1,
        MARGIN_LEFT,
        currentTextY
      );

      currentTextY += 3;
    }

    // ---------- ADDRESS LINE 2 ----------
    if (addrLine2.trim()) {
      doc.text(
        addrLine2,
        MARGIN_LEFT,
        currentTextY
      );

      currentTextY += 3;
    }

    // ---------- PHONE + GSTIN ----------
    const phoneParts = [
      documentConfig?.fromData?.phone,
      documentConfig?.fromData?.additionalPhone,
    ].filter(Boolean);

    const phone = phoneParts.join(", ");
    const gstin = documentConfig?.fromData?.gstnumber || "";

    let currentX = MARGIN_LEFT;

    // Phone
    if (phone) {
      doc.setFont("Inter-Bold", "bold");

      doc.text(
        "Ph:",
        currentX,
        currentTextY
      );

      currentX += doc.getTextWidth("Ph: ");

      doc.setFont("Inter-Regular", "normal");

      doc.text(
        phone,
        currentX,
        currentTextY
      );

      currentX += doc.getTextWidth(`${phone}  `);
    }

    // GSTIN
    if (gstin) {
      doc.setFont("Inter-Bold", "bold");

      doc.text(
        "GSTIN:",
        currentX,
        currentTextY
      );

      currentX += doc.getTextWidth("GSTIN: ");

      doc.setFont("Inter-Regular", "normal");

      doc.text(
        gstin,
        currentX,
        currentTextY
      );
    }

    // ---------- UPDATE Y ----------
    y = currentTextY + 2;

  } catch (e) {
    console.warn("Header layout failed", e);
    y += 15;
  }


    y +=  documentConfig?.image ? 1:5;

  if (documentConfig?.toData) {
  let rowHeight = 6;

  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor || [200, 200, 200]
  );
  doc.setLineWidth(0.2);

  // ---------------- LEFT ----------------
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text(documentConfig?.toLabel, MARGIN_LEFT + 2, y + 4);

  doc.setFont("Inter-Regular", "normal");
  doc.text(documentConfig?.toData?.name || "", MARGIN_LEFT + 2, y + 8);

  rowHeight += 4;

  const fullAddress = [
    documentConfig?.toData?.addressline1,
    documentConfig?.toData?.addressline2,
    documentConfig?.toData?.district,
    documentConfig?.toData?.state,
  ]
    .filter(Boolean)
    .join(", ");

  const pinCode = documentConfig?.toData?.pincode
    ? ` - ${documentConfig?.toData.pincode}`
    : "";

  if (fullAddress || pinCode) {
    const wrapped = doc.splitTextToSize(
      fullAddress + pinCode,
      pageW / 2 + 10
    );

    doc.text(wrapped, MARGIN_LEFT + 2, y + 12);

    rowHeight += wrapped.length * 3.5;
  }

  // ---------------- RIGHT ----------------

  const mrpStartX = MARGIN_LEFT + 156;
  const rightSectionStart = mrpStartX + 2;

  const customerInfo = [
    { label: "Ph:", value: documentConfig?.toData?.phone },
    { label: "GSTIN:", value: documentConfig?.toData?.gstnumber },
    { label: "Reference No:", value: documentConfig?.toData?.referenceid },
    { label: "FSSAI:", value: documentConfig?.toData?.fssai },
    { label: "CIN:", value: documentConfig?.toData?.cinnumber },
    { label: "PAN:", value: documentConfig?.toData?.pannumber },
  ].filter(item => item.value);

  let infoY = y + 4;

  customerInfo.forEach(({ label, value }) => {

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7.5);
    doc.text(label, rightSectionStart, infoY);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      String(value),
      rightSectionStart + doc.getTextWidth(label) + 1,
      infoY
    );

    infoY += 4.5;
  });

  // Increase height if right side is taller
  rowHeight = Math.max(rowHeight, infoY - y + 1);

  // Draw divider AFTER calculating final height
  doc.line(mrpStartX, y, mrpStartX, y + rowHeight);

  // Draw outer rectangle
  doc.rect(
    MARGIN_LEFT,
    y,
    MARGIN_RIGHT - MARGIN_LEFT,
    rowHeight
  );

  y += rowHeight;
}
  /* -------------------------------------------------
     PRODUCT TABLE1
  ------------------------------------------------- */

  const rowCount = documentConfig?.items.length;
  const standardHeight = 4;
  const targetTotalHeight = 40;
  const extraSpace =
    rowCount > 0 && rowCount <= 5
      ? targetTotalHeight - rowCount * standardHeight
      : 0;
  autoTable(doc, {
    startY: y,
    theme: "plain",
    rowPageBreak: "avoid",
    margin: {
      top: PADDING,
      left: PADDING,
      right: PADDING,
      bottom: pageH - HALF_PAGE_HEIGHT + PADDING,
    },
    styles: {
      font: "Inter-Regular",
      fontSize: 6.5,
      cellPadding: 1.5,
      valign: "top",
      lineWidth: 0, // No default borders
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      fillColor: selectedTheme?.templateTheme?.primaryColor || [216, 27, 96], // Pink like image
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      lineWidth: 0.2,
      lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    },
    bodyStyles: {
      minCellHeight: Math.max(2, standardHeight),
      valign: "top",
    },
    head: [["#", "ITEM", "RATE", "QTY", "LANDING  COST", "Tax Val", "AMOUNT"]],
    body: documentConfig?.items.map((it, i) => [
      i + 1,
      it.hsnsac ? `${it.title}\nHSN: ${it.hsnsac}` : it.title,

      `${it.baseprice}`,
      `${it.quantity} ${(() => {
        const unitCode = it?.isSecondaryUnit
          ? it?.unitConfig?.secondaryUnitCode
          : it?.unitConfig?.primaryUnitCode;
        return unitCode || "";
      })()}`,
      `${it.lineTotalLandingCost}`,
      `${it.lineTotalBasePrice}`,
      `${it.lineGrandTotal}`,
    ]),
    columnStyles: {
      0: { cellWidth: 13, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 22, halign: "right" },
      3: { cellWidth: 11, halign: "center" },
      4: { cellWidth: 22, halign: "right" },
      5: { cellWidth: 22, halign: "right" },
      6: { cellWidth: 22, halign: "right" },
    },
    didParseCell: function (data) {
      if (data.section === "head") {
        const colId = data.column.index;
          if ([0,1,2, 3,4,5,6].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
      if (data.section === "body") {
        data.cell.styles.lineWidth = {
          left: 0.1,
          right: 0.1,
          top: 0,
          bottom: 0,
        };
        data.cell.styles.lineColor = selectedTheme?.templateTheme
          ?.borderColor || [200, 200, 200]; // Black vertical lines
        if (data.row.index === rowCount - 1) {
          data.cell.styles.minCellHeight = standardHeight + extraSpace;
        }
        if (data.row.index === rowCount - 1) {
          data.cell.styles.lineWidth.bottom = 0.1;
        }
      }
    },
    didDrawPage: function (data) {
      const finalY = data.cursor.y;
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
      doc.line(PADDING, finalY, pageW - PADDING, finalY);
    },
  });
  let finalY = doc.lastAutoTable.finalY;

  const borderColor = selectedTheme?.templateTheme?.borderColor || [
    200, 200, 200,
  ];
  const rowH = 5;
  const sigSectionHeight = 15;
  const taxRowsCount = isInterState ? 1 : 2;
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.1);
  const totalFooterHeight =
    rowH + Math.max(sigSectionHeight, taxRowsCount * rowH) + 10; // +10 for branding/padding
  if (finalY + totalFooterHeight > HALF_PAGE_HEIGHT - 5) {
    doc.addPage();
    finalY = PADDING;
  }

  const rightSectionWidth = 66;
  const amountWidth = 22;
  const totalLabelX = MARGIN_RIGHT - rightSectionWidth;
  const totalValueX = MARGIN_RIGHT - amountWidth;

  // --- 2. TOP ROW: IN WORDS & TOTAL ---
  doc.rect(PADDING, finalY, pageW - PADDING * 2, rowH);
  doc.line(totalLabelX, finalY, totalLabelX, finalY + rowH);
  doc.line(totalValueX, finalY, totalValueX, finalY + rowH);

  let textY = finalY + rowH / 2 + 1;
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(7);
  doc.text("In Words:", PADDING + 2, textY);
  doc.setFont("Inter-Regular", "normal");
  const words = documentConfig?.amountInWords || "Zero Rupees Only";
  doc.text(words, PADDING + 15, textY, {
    maxWidth: totalLabelX - PADDING - 17,
  });

  doc.setFont("Inter-Bold", "bold");
  doc.text("TOTAL", totalLabelX + 2, textY);
  doc.text(`${documentConfig?.grandTotal}`, MARGIN_RIGHT - 1, textY, { align: "right" });

  // --- 3. THE "SAME LINE" SECTION (Signature Left, Taxes Right) ---
  let currentTaxY = finalY + rowH; // This is the shared starting line

  // Total height for this area
  const totalHeight = Math.max(sigSectionHeight, taxRowsCount * rowH);
  if (isInterState) {
    doc.rect(totalLabelX, currentTaxY, rightSectionWidth, rowH);
    doc.line(totalValueX, currentTaxY, totalValueX, currentTaxY + rowH);
    doc.setFont("Inter-Bold", "bold");
    doc.text("IGST", totalLabelX + 2, currentTaxY + 3.5);
    doc.setFont("Inter-Regular", "normal");
    doc.text(`${documentConfig?.igstAmount || 0}`, MARGIN_RIGHT - 2, currentTaxY + 3.5, {
      align: "right",
    });
  } else {
    // CGST Row
    doc.rect(totalLabelX, currentTaxY, rightSectionWidth, rowH);
    doc.line(totalValueX, currentTaxY, totalValueX, currentTaxY + rowH);
    doc.setFont("Inter-Regular", "normal");
    doc.text("CGST", totalLabelX + 2, currentTaxY + 3.5);
    doc.setFont("Inter-Regular", "normal");
    doc.text(`${documentConfig?.cgstAmount || 0}`, MARGIN_RIGHT - 2, currentTaxY + 3.5, {
      align: "right",
    });

    // SGST Row (Directly below CGST)
    doc.rect(totalLabelX, currentTaxY + rowH, rightSectionWidth, rowH);
    doc.line(
      totalValueX,
      currentTaxY + rowH,
      totalValueX,
      currentTaxY + rowH * 2,
    );
    doc.setFont("Inter-Regular", "normal");
    doc.text("SGST", totalLabelX + 2, currentTaxY + rowH + 3.5);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      `${documentConfig?.sgstAmount || 0}`,
      MARGIN_RIGHT - 2,
      currentTaxY + rowH + 3.5,
      { align: "right" },
    );
  }
  let sigBottomY = currentTaxY + totalHeight - 2;
 const paymentData = [
    { amount: documentConfig?.cashPaid || 0, label: "Cash", val: documentConfig?.cashPaid },
    { amount: documentConfig?.cardPaid || 0, label: "Card", val: documentConfig?.cardPaid },
    { amount: documentConfig?.upiPaid || 0, label: "UPI", val: documentConfig?.upiPaid },
    { amount:documentConfig?.onlinePaid || 0, label: "Online", val:documentConfig?.onlinePaid },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);
  currentTaxY+=5
   let paymentEndY = currentTaxY;

if (paymentData.length > 0 && !isPreSale) {
  paymentData.forEach((pay, index) => {
    const xPos = index % 2 === 0 ? PADDING : PADDING + 45;
    const yPos = paymentEndY + Math.floor(index / 2) * 5;

    doc.setFont("Inter-Bold", "bold");
    doc.text(`${pay.val}`, xPos, yPos);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      ` Paid via ${pay.label}`,
      xPos + doc.getTextWidth(`${pay.val}`) + 1,
      yPos
    );
  });

  // Calculate payment section height
  const paymentRows = Math.ceil(paymentData.length / 2);
  paymentEndY = sigBottomY + paymentRows * 5 -5;
}
 let currentSectionY = paymentEndY ;
  // let currentSectionY = currentTaxY + totalHeight + 5;
  if (documentConfig?.bankDetails) {
      const bankStartY = currentSectionY;
  
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0);
      doc.text("Bank Details:", PADDING , bankStartY);
  
     const bankInfo = [
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],    
    ].filter(([_, value]) => Boolean(value));
    
  
      let currentBankY = bankStartY + 4;
      bankInfo.forEach(([label, value]) => {
        doc.setFont("Inter-Regular", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(label, PADDING, currentBankY);
        
        doc.setFont("Inter-Regular", "normal");
        doc.setTextColor(0);
        doc.text(String(value || ""), PADDING + 25, currentBankY);
        
        currentBankY += 3.8;
      });
  
      // Generate & Draw QR Code alongside Bank Details
      const upiId = storeSettings?.paymentSettings?.upiId;
      const beneficiaryName = storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";
  
      if (upiId) {
        try {
          const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(beneficiaryName)}&cu=INR`;
          const qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
            errorCorrectionLevel: "L",
            margin: 1,
            scale: 4,
          });
  
          const qrSize = 18;
          const qrX = 90 - qrSize - 4; // Positioned before vertical line
          const qrY = bankStartY + 1;
  
          doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
        } catch (err) {
          console.warn("QR Code generation failed", err);
        }
      }
  
      currentSectionY = currentBankY;
    }
     if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;
      const base64 = await urlToBase64(signimg);
      const { width, height } = await loadImageDimensions(base64);
      const ratio = Math.min(25 / width, 8 / height);
      const imgW = width * ratio;
      const imgH = height * ratio;
        sigBottomY += 3;
      doc.addImage(base64, "PNG",   MARGIN_RIGHT - imgW, sigBottomY, imgW, imgH);
    } catch (e) {
      console.warn("Signature failed", e);
    }
  } else {
    sigBottomY = currentTaxY + totalHeight - 3;
  }

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(6);
 sigBottomY += 10;
  doc.text("Authorised Signature", MARGIN_RIGHT, sigBottomY, {
    align: "right",
  });
  if (documentConfig?.remarks?.trim()) {

  const remarkWidth = pageW - (PADDING * 2);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);

  const remarkLines = doc.splitTextToSize(
    capitalizeFirstLetter(documentConfig.remarks),
    remarkWidth - 6
  );

  const lineHeight = 3.5;
  const paddingTop = 2;
  const paddingBottom = 2;

  const remarkHeight =
    (remarkLines.length * lineHeight) + paddingTop + paddingBottom;

  if (
    currentSectionY + remarkHeight + 20 >
    HALF_PAGE_HEIGHT - 5
  ) {
    doc.addPage();
    currentSectionY = PADDING + 5;
  }

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text("Remark", PADDING, currentSectionY);

  currentSectionY += 2;

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);

  let remarkY = currentSectionY + 2;

  remarkLines.forEach(line => {
    doc.text(line, PADDING , remarkY);
    remarkY += lineHeight;
  });

  currentSectionY += remarkHeight + 4;
}

  if (storeSettings?.negobillBranding) {
    const minGapY = y + 3;
    const bottomLimitY = HALF_PAGE_HEIGHT - 5;
    let finalBrandingY = Math.max(minGapY, bottomLimitY);
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);
    doc.text(normalText, MARGIN_LEFT, finalBrandingY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, MARGIN_LEFT + normalW, finalBrandingY);
  }

  return doc;
};

export const generateBILL_HA4_3PDF = async (
 data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
   const {
    documentConfig,
    hasPayment,   
    
  } = invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const centerX = pageW / 2;
  let y = PADDING; // Starting height
   const documentTitle = documentConfig?.title ||" ";
  const invLabels = documentConfig?.numberLabel;


const imagePath = documentConfig?.image;

try {
  // ---------- HEADER HEIGHT ----------
  let finalH = 12;

  // ---------- INVOICE DETAILS ----------
  const invLabel = invLabels || "";
  const invVal = documentConfig?.number || "-";
  const invDateTime = documentConfig?.Date?.time || "";

  const dateStr = `DATE: ${documentConfig?.Date?.date || ""}`;
  const timeStr = `TIME: ${invDateTime}`;

  // ---------- LOGO ----------
  if (imagePath) {
    const logoBase64 = await urlToBase64(imagePath);
    const { width, height } = await loadImageDimensions(logoBase64);

    const maxWidth = 30;
    const maxHeight = 12;

    const ratio = Math.min(
      maxWidth / width,
      maxHeight / height
    );

    const finalW = width * ratio;
    finalH = height * ratio;

    doc.addImage(
      logoBase64,
      "PNG",
      MARGIN_LEFT,
      y,
      finalW,
      finalH
    );
  }

  // ---------- DOCUMENT TITLE ----------
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100);

  doc.text(
    documentTitle || "",
    centerX,
    y + 3,
    { align: "center" }
  );

  // ---------- STORE NAME ----------
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0);

  doc.text(
    documentConfig?.fromData?.title?.toUpperCase() || "",
    centerX,
    y + 8,
    { align: "center" }
  );

  // ---------- INVOICE NUMBER ----------
  doc.setFontSize(7);
  doc.setFont("Inter-Regular", "normal");

  const invValW = doc.getTextWidth(invVal);
  const invLabelW = doc.getTextWidth(invLabel);

  doc.text(
    invLabel,
    MARGIN_RIGHT - invValW - invLabelW,
    y + 2
  );

  doc.setFont("Inter-Bold", "bold");

  doc.text(
    invVal,
    MARGIN_RIGHT,
    y + 2,
    { align: "right" }
  );

  // ---------- DATE / TIME LINE ----------
  doc.setFont("Inter-Regular", "normal");

  const dateWidth = doc.getTextWidth(dateStr);
  const timeWidth = doc.getTextWidth(timeStr);

  const totalDateTimeWidth = dateWidth + timeWidth;

  const lineStartX =
    MARGIN_RIGHT - totalDateTimeWidth - 1;

  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor
  );

  doc.setLineWidth(0.1);

  doc.line(
    lineStartX,
    y + 3.5,
    MARGIN_RIGHT,
    y + 3.5
  );

  // Date
  doc.text(
    dateStr,
    MARGIN_RIGHT - timeWidth - 1,
    y + 6,
    { align: "right" }
  );

  // Time
  doc.text(
    timeStr,
    MARGIN_RIGHT,
    y + 6,
    { align: "right" }
  );

  // ---------- ADDRESS ----------
  // If image exists → address below logo.
  // If image doesn't exist → use the logo area.
  let addrY;

  if (imagePath) {
    addrY = y + finalH + 4;
  } else {
    addrY = y + 4;
  }

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);

  let currentTextY = addrY;

  const addrLine1 = [
    documentConfig?.fromData?.addressline1,
    documentConfig?.fromData?.addressline2,
  ]
    .filter(Boolean)
    .join(", ");

  const addrLine2 = [
    documentConfig?.fromData?.district,
    documentConfig?.fromData?.state,
    documentConfig?.fromData?.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  // ---------- ADDRESS LINE 1 ----------
  if (addrLine1.trim()) {
    doc.text(
      addrLine1,
      MARGIN_LEFT,
      currentTextY
    );

    currentTextY += 3;
  }

  // ---------- ADDRESS LINE 2 ----------
  if (addrLine2.trim()) {
    doc.text(
      addrLine2,
      MARGIN_LEFT,
      currentTextY
    );

    currentTextY += 3;
  }

  // ---------- PHONE + GSTIN ----------
  const phoneParts = [
    documentConfig?.fromData?.phone,
    documentConfig?.fromData?.additionalPhone,
  ].filter(Boolean);

  const phone = phoneParts.join(", ");
  const gstin = documentConfig?.fromData?.gstnumber || "";

  let currentX = MARGIN_LEFT;

  // Phone
  if (phone) {
    doc.setFont("Inter-Bold", "bold");

    doc.text(
      "Ph:",
      currentX,
      currentTextY
    );

    currentX += doc.getTextWidth("Ph: ");

    doc.setFont("Inter-Regular", "normal");

    doc.text(
      phone,
      currentX,
      currentTextY
    );

    currentX += doc.getTextWidth(`${phone}  `);
  }

  // GSTIN
  if (gstin) {
    doc.setFont("Inter-Bold", "bold");

    doc.text(
      "GSTIN:",
      currentX,
      currentTextY
    );

    currentX += doc.getTextWidth("GSTIN: ");

    doc.setFont("Inter-Regular", "normal");

    doc.text(
      gstin,
      currentX,
      currentTextY
    );
  }

  // ---------- UPDATE Y ----------
  y = currentTextY + 2;

} catch (e) {
  console.warn("Header layout failed", e);
  y += 15;
}


  y +=  documentConfig?.image ? 1:5;

  y += 1;

  if (documentConfig?.toData) {
  let rowHeight = 6;

  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor || [200, 200, 200]
  );
  doc.setLineWidth(0.2);

  // ---------------- LEFT ----------------
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text(documentConfig?.toLabel, MARGIN_LEFT + 2, y + 4);

  doc.setFont("Inter-Regular", "normal");
  doc.text(documentConfig?.toData?.name || "", MARGIN_LEFT + 2, y + 8);

  rowHeight += 4;

  const fullAddress = [
    documentConfig?.toData?.addressline1,
    documentConfig?.toData?.addressline2,
    documentConfig?.toData?.district,
    documentConfig?.toData?.state,
  ]
    .filter(Boolean)
    .join(", ");

  const pinCode = documentConfig?.toData?.pincode
    ? ` - ${documentConfig?.toData.pincode}`
    : "";

  if (fullAddress || pinCode) {
    const wrapped = doc.splitTextToSize(
      fullAddress + pinCode,
      pageW / 2 + 10
    );

    doc.text(wrapped, MARGIN_LEFT + 2, y + 12);

    rowHeight += wrapped.length * 3.5;
  }

  // ---------------- RIGHT ----------------

  const mrpStartX = MARGIN_LEFT + 160;
  const rightSectionStart = mrpStartX + 2;

  const customerInfo = [
    { label: "Ph:", value: documentConfig?.toData?.phone },
    { label: "GSTIN:", value: documentConfig?.toData?.gstnumber },
    { label: "Reference No:", value: documentConfig?.toData?.referenceid },
    { label: "FSSAI:", value: documentConfig?.toData?.fssai },
    { label: "CIN:", value: documentConfig?.toData?.cinnumber },
    { label: "PAN:", value: documentConfig?.toData?.pannumber },
  ].filter(item => item.value);

  let infoY = y + 4;

  customerInfo.forEach(({ label, value }) => {

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7.5);
    doc.text(label, rightSectionStart, infoY);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      String(value),
      rightSectionStart + doc.getTextWidth(label) + 1,
      infoY
    );

    infoY += 4.5;
  });

  // Increase height if right side is taller
  rowHeight = Math.max(rowHeight, infoY - y + 1);

  // Draw divider AFTER calculating final height
  doc.line(mrpStartX, y, mrpStartX, y + rowHeight);

  // Draw outer rectangle
  doc.rect(
    MARGIN_LEFT,
    y,
    MARGIN_RIGHT - MARGIN_LEFT,
    rowHeight
  );

  y += rowHeight;
}
  /* -------------------------------------------------
     PRODUCT TABLE1
  ------------------------------------------------- */

  const rowCount = documentConfig?.items.length;
  const standardHeight = 4;
  const targetTotalHeight = 40;
  const extraSpace =
    rowCount > 0 && rowCount <= 5
      ? targetTotalHeight - rowCount * standardHeight
      : 0;
  autoTable(doc, {
    startY: y,
    theme: "plain",
    rowPageBreak: "avoid",
    margin: {
      top: PADDING,
      left: PADDING,
      right: PADDING,
      bottom: pageH - HALF_PAGE_HEIGHT + PADDING,
    },
    styles: {
      font: "Inter-Regular",
      fontSize: 6.5,
      cellPadding: 1.5,
      valign: "top",
      lineWidth: 0, // No default borders
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      fillColor: selectedTheme?.templateTheme?.primaryColor || [216, 27, 96], // Pink like image
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      lineWidth: 0.2,
      lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    },
    bodyStyles: {
      minCellHeight: Math.max(2, standardHeight),
      valign: "top",
    },
    head: [["#", "ITEM", "RATE", "QTY", "AMOUNT"]],
    body: documentConfig?.items.map((it, i) => [
      i + 1,
      it.hsnsac ? `${it.title}\nHSN: ${it.hsnsac}` : it.title,

      `${it.baseprice}`,
      `${it.quantity} ${(() => {
        const unitCode = it?.isSecondaryUnit
          ? it?.unitConfig?.secondaryUnitCode
          : it?.unitConfig?.primaryUnitCode;
        return unitCode || "";
      })()}`,
      `${it.lineTotalBasePrice}`,
      `${it.lineGrandTotal}`,
    ]),
    columnStyles: {
      0: { cellWidth: 13, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 25, halign: "right" },
      3: { cellWidth: 15, halign: "center" },
      4: { cellWidth: 25, halign: "right" },
    },
    didParseCell: function (data) {
      if (data.section === "head") {
        const colId = data.column.index;
          if ([0,1,2, 3,4,5,6].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }
      if (data.section === "body") {
        data.cell.styles.lineWidth = {
          left: 0.1,
          right: 0.1,
          top: 0,
          bottom: 0,
        };
        data.cell.styles.lineColor = selectedTheme?.templateTheme
          ?.borderColor || [200, 200, 200]; // Black vertical lines
        if (data.row.index === rowCount - 1) {
          data.cell.styles.minCellHeight = standardHeight + extraSpace;
        }
        if (data.row.index === rowCount - 1) {
          data.cell.styles.lineWidth.bottom = 0.1;
        }
      }
    },
    didDrawPage: function (data) {
      const finalY = data.cursor.y;
      doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
      doc.line(PADDING, finalY, pageW - PADDING, finalY);
    },
  });
  let finalY = doc.lastAutoTable.finalY;

  const footerRowHeight = 4; // Height of the "Total" bar
  const bottomSectionHeight = 20;
  const requiredHeight = footerRowHeight + bottomSectionHeight;
  const maxY = HALF_PAGE_HEIGHT - PADDING;
  if (finalY + requiredHeight > maxY) {
    doc.addPage();
    finalY = PADDING;
  }
  const borderColor = selectedTheme?.templateTheme?.borderColor || [
    200, 200, 200,
  ];
  const textY = finalY + footerRowHeight / 2 + 1;
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.1);

  doc.rect(PADDING, finalY, pageW - PADDING * 2, footerRowHeight);
  const totalLabelX = MARGIN_RIGHT - 65;
  const totalValueX = MARGIN_RIGHT - 25;
  // Vertical line to separate "In Words" from "Total"
  doc.line(totalLabelX, finalY, totalLabelX, finalY + footerRowHeight);
  // Vertical line to separate "Total" label from "Value"
  doc.line(totalValueX, finalY, totalValueX, finalY + footerRowHeight);

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(7);
  doc.text("In Words:", PADDING + 2, textY);
  doc.setFont("Inter-Regular", "normal");
  const words = documentConfig?.amountInWords || "";
  doc.text(words, PADDING + 15, textY, {
    maxWidth: totalLabelX - PADDING - 17,
  });
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(6.5);
  doc.text("TOTAL", totalLabelX + 38, textY, { align: "right" });
  doc.text(`${documentConfig?.grandTotal}`, MARGIN_RIGHT - 1, textY, { align: "right" });

  // --- 2. PAYMENT & SIGNATURE SECTION (Below the total bar) ---
  let bottomSectionY = finalY + footerRowHeight;
  let sigTextY;
  if (bottomSectionY + bottomSectionHeight > HALF_PAGE_HEIGHT - PADDING) {
    doc.addPage();
   
    bottomSectionY = PADDING;
    sigTextY = bottomSectionHeight - 2;
  } else {
    sigTextY = finalY + bottomSectionHeight - 2;
  } // const bottomSectionHeight = 20;
const sectionY = bottomSectionY + 5;
//leftside 
const qrSize = 18;
 let currentBankY = sectionY ;
if (documentConfig?.bankDetails) {
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  doc.text("Bank Details:", PADDING , sectionY);

 const bankInfo = [
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],    
    ].filter(([_, value]) => Boolean(value));
    

 currentBankY+=4

  bankInfo.forEach(([label, value]) => {
    doc.setFont("Inter-Regular", "normal");
    doc.text(label, PADDING , currentBankY);
    doc.text(String(value), PADDING  + 25, currentBankY);
    currentBankY += 3.8;
  });


  // QR Code
  const upiId = storeSettings?.paymentSettings?.upiId;
  const beneficiaryName =
    storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";

  if (upiId) {
    try {
      const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
        beneficiaryName
      )}&cu=INR`;

      const qrCodeDataUrl = await QRCode.toDataURL(upiUrl);

const qrX = PADDING + 55;

      doc.addImage(
        qrCodeDataUrl,
        "PNG",
        qrX,
        sectionY,
        qrSize,
        qrSize
      );
    } catch (err) {
      console.warn(err);
    }
  }
}

  // Right SIDE: Payment Details
  let currentPayY = sectionY
  const paymentData = [
    { amount: documentConfig?.cashPaid || 0, label: "Cash", val: documentConfig?.cashPaid },
    { amount: documentConfig?.cardPaid || 0, label: "Card", val: documentConfig?.cardPaid },
    { amount: documentConfig?.upiPaid || 0, label: "UPI", val: documentConfig?.upiPaid },
    { amount:documentConfig?.onlinePaid || 0, label: "Online", val:documentConfig?.onlinePaid },
  ].filter((p) =>Math.abs(parseAmount(p.amount)) > 0);

 if (hasPayment) {
 const paymentStartX = pageW - 80; // Adjust this value if needed

paymentData.forEach((pay, index) => {
  // Keep the same 2-column layout
  const xPos = index % 2 === 0 ? paymentStartX : paymentStartX + 45;
  const yPos = currentPayY + Math.floor(index / 2) * 5;

  doc.setFont("Inter-Bold", "bold");
  doc.text(`${pay.val}`, xPos, yPos);

  doc.setFont("Inter-Regular", "normal");
  doc.text(
    ` Paid via ${pay.label}`,
    xPos + doc.getTextWidth(`${pay.val}`) + 1,
    yPos
  );

  });
 }


 // =====================================
// REMARK SECTION
// =====================================

const bankEndY = currentBankY;
const paymentEndY = sectionY + Math.ceil(paymentData.length / 2) * 5;

let remarkStartY = sectionY;
let qrEndY = sectionY;
if (documentConfig?.bankDetails && hasPayment) {
  remarkStartY = Math.max(bankEndY, paymentEndY, qrEndY) + 4;
} else if (documentConfig?.bankDetails) {
  remarkStartY = Math.max(bankEndY, qrEndY) + 4;
} else if (hasPayment) {
  remarkStartY = paymentEndY + 4;
} else {
  remarkStartY = sectionY;
}
if (documentConfig?.remarks?.trim()) {

  const remarkWidth = pageW - (PADDING * 2);

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);

  // Check space
  const remarkLines = doc.splitTextToSize(
    capitalizeFirstLetter(documentConfig?.remarks),
    remarkWidth - 6
  );

  const lineHeight = 3.5;
    const paddingTop = 2;
  const paddingBottom = 2;
    const remarkHeight =
    (remarkLines.length * lineHeight) + paddingTop + paddingBottom;

  if (remarkStartY + remarkHeight + 10 > HALF_PAGE_HEIGHT) {
    doc.addPage();
    remarkStartY = PADDING+2;
  }

  // Heading
  doc.text("Remark", PADDING, remarkStartY);

  // Border
  doc.setDrawColor(
    selectedTheme?.templateTheme?.borderColor || 180
  );
  doc.setLineWidth(0.2);

  // Text
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);

  let txtY = remarkStartY + 3;

  remarkLines.forEach(line => {
    doc.text(line, PADDING , txtY);
    txtY += lineHeight;
  });

  remarkStartY += remarkHeight + 4;
} 
let signatureY = remarkStartY ;
if (signatureY + 20 > HALF_PAGE_HEIGHT) {
  doc.addPage();
  signatureY = PADDING + 2;
}

const signMaxWidth = 25;
const signX = pageW - PADDING - signMaxWidth;

if (storeSettings?.invoiceSettings?.signature) {
  try {
    const signimg =  uRLs.IMAGE_URL +storeSettings.invoiceSettings.signature;
    const base64 = await urlToBase64(signimg);
    const { width, height } = await loadImageDimensions(base64);

    const maxWidth = 25;
    const maxHeight = 10;
    const ratio = Math.min(maxWidth / width, maxHeight / height);

    const imgW = width * ratio;
    const imgH = height * ratio;

    doc.addImage(
      base64,
      signimg.endsWith(".png") ? "PNG" : "JPEG",
      signX + (maxWidth - imgW) / 2,
      signatureY,
      imgW,
      imgH
    );

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(6);
    doc.text(
      "Authorised Signature",
      signX + maxWidth / 2,
      signatureY + imgH + 4,
      { align: "center" }
    );
  } catch (e) {
    console.warn("Signature failed", e);
  }
}
  if (storeSettings?.negobillBranding) {
    // const finalBrandingY = remarkStartY + 3;
    const minGapY = y + 3;
    const bottomLimitY = HALF_PAGE_HEIGHT - 5;
    let finalBrandingY = Math.max(minGapY, bottomLimitY);
    const normalText = "Powered by ";
    const boldText = "NegoBill.com";
    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);
    doc.text(normalText, MARGIN_LEFT, finalBrandingY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, MARGIN_LEFT + normalW, finalBrandingY);
  }

  return doc;
};

const CompactInvoice = ({ data,
  qrCodeDataUrl,
  selectedTheme,  
  invoiceConfig,}) => {
  const { storeSettings } = usePermission();  
  const {
  documentConfig,  
  showTaxDetails,    
  showBillDetails,   
 hasPayment,
 isInterState
} = invoiceConfig; 

     
  return (
    <>
      {showTaxDetails ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in p-2">
                <div style={{ display: "flex", paddingBottom: "10px" }}>
                  <div style={{ flex: "1" }}>
                    <div>
                      {documentConfig?.image && (
                        <img
                          src={
                            documentConfig?.image}
                          className="img-rounded logo"
                          height="40"
                          alt="Logo"
                        />
                      )}
                    </div>

                    <div className="font-12">
                      {documentConfig?.fromData?.addressline1}
                    </div>
                    <div className="font-12">
                      {documentConfig?.fromData?.addressline2}{" "}
                      {documentConfig?.fromData?.pincode || ""}
                    </div>
                    <div className="font-12">
                      <span className="fw-bold">Ph:</span>{" "}
                       {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )}{" "}
                             {documentConfig?.fromData?.gstnumber &&(<>
                      <span className="fw-bold">GSTIN:</span>{" "}
                      {documentConfig?.fromData?.gstnumber}
                            </> )}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "center" }}>
                    <div className="font-14" style={{ color: "#555" }}>
                      {documentConfig?.title}
                    </div>
                    <div
                      className="font-15"
                      style={{
                        marginTop: "5px",
                        fontWeight: "800",
                        textTransform: "uppercase",
                        lineHeight: "1",
                      }}
                    >
                      {documentConfig?.fromData?.title || "COMPANY NAME"}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "right" }}>
                    <div
                      style={{ display: "inline-block", textAlign: "right" }}
                    >
                      <div
                        className=" font-10"
                        style={{
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          marginBottom: "4px",
                        }}
                      >
                        {documentConfig?.numberLabel}
                        <span className="fw-bold"> {documentConfig?.number}</span>
                      </div>
                      <div className="d-flex justify-content-end font-10">
                        {/* <span>DATE: {saleTime?.date}</span> */}
                        <span> DATE: {documentConfig?.Date?.date}</span>
                        <span className="ms-3">TIME:{documentConfig?.Date?.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {documentConfig?.toData && (
                  <div
                    className="d-flex "
                    style={{
                      border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                    }}
                  >
                    <div
                      style={{
                        flex: "1",
                        padding: "5px",
                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                      }}
                    >
                      <span className="fw-bold font-12">
                        Customer Details :
                      </span>
                      <div className="font-12 ">{documentConfig?.toData?.name}</div>
                      <div className="font-12">
                        {" "}
                        {documentConfig?.toData?.addressline1 &&
                          `${documentConfig?.toData?.addressline1}, `}
                        {documentConfig?.toData?.addressline2 &&
                          `${documentConfig?.toData?.addressline2}, `}
                        {documentConfig?.toData?.district &&
                          `${documentConfig?.toData?.district}, `}
                        {documentConfig?.toData?.pincode &&
                          ` - ${documentConfig?.toData?.pincode}`}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "178px",
                        padding: "5px",
                        WebkitFontSmoothing: "antialiased",
                        textRendering: "optimizeLegibility",
                      }}
                    >
                      <div className="font-12">
                        <span className="fw-bold">Ph:</span>{" "}
                        {documentConfig?.toData?.phone || ""}
                      </div>
                      {documentConfig?.toData?.gstnumber &&(
                     <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                GSTIN:
                                              </span>{" "}
                                              {documentConfig?.toData?.gstnumber ||
                                                ""}
                                            </div> 
                      )}
                                             {documentConfig?.toData?.pannumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                PAN:
                                              </span>{" "}
                                              {documentConfig?.toData?.pannumber ||
                                                ""}
                                            </div>
                                            )}
                                            {documentConfig?.toData?.fssai &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                FASSI:
                                              </span>{" "}
                                              {documentConfig?.toData?.fssai ||
                                                ""}
                                            </div>
                                            )}
                                              {documentConfig?.toData?.cinnumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                CIN:
                                              </span>{" "}
                                              {documentConfig?.toData?.cinnumber ||
                                                ""}
                                            </div>
                                              )}
                                            {documentConfig?.toData?.referenceid &&(
                                              <div className=" font-12">
                                              {" "}
                                              <span className="">
                                                Reference No:
                                              </span>{" "}
                                              {documentConfig?.toData?.referenceid ||
                                                ""}
                                            </div>
                                              )}
                    </div>
                  </div>
                )}
                <table
                  width="100%"
                  className="mt-0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr className=" fw-bold">
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "40px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "40px" }}
                      >
                        {" "}
                        #
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        // style={{ border: "1px solid ", padding: "5px", textAlign: "left" }}
                      >
                        ITEM
                      </th>

                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "80px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ", padding: "5px", width: "80px" }}
                      >
                        RATE
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "60px" }}
                      >
                        QTY
                      </th>
                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "60px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        LANDING COST
                      </th>
                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "60px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "80px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        TAX VAL
                      </th>
                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "100px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentConfig?.items?.map((item, index) => (
                      <tr
                        key={index}
                        className="font-13"
                        style={{ height: "30px" }}
                      >
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,

                            textAlign: "center",
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            padding: "0 5px",
                          }}
                        >
                          {item?.title}
                        </td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          {item?.baseprice}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "center",
                          }}
                        >
                          {item?.quantity}
                          {(() => {
                            const unitCode = item?.isSecondaryUnit
                              ? item?.unitConfig?.secondaryUnitCode
                              : item?.unitConfig?.primaryUnitCode;
                            return unitCode || "";
                          })()}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          {item?.lineTotalLandingCost}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          {item?.lineTotalBasePrice}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          {item?.lineGrandTotal}
                        </td>
                      </tr>
                    ))}

                    {[
                      ...Array(
                        Math.max(0, 5 - (documentConfig?.items?.length || 0)),
                      ),
                    ].map((_, i) => (
                      <tr key={`empty-${i}`} style={{ height: "50px" }}>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Row 1: In Words & Total */}
                    <tr className="font-10">
                      <td
                        colSpan="4"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "left",
                          padding: "5px",
                        }}
                      >
                        <span className="fw-bold">In Words:</span>{" "}
                        {documentConfig?.amountInWords}
                      </td>
                      <td
                        colSpan="2"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        TOTAL
                      </td>
                      <td
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        {documentConfig?.grandTotal}
                      </td>
                    </tr>
                    <tr className="font-10">
                      <td
                        colSpan="4"
                        rowSpan={isInterState ? 1 : 2}
                        style={{
                          textAlign: "left",
                          
                          verticalAlign: "top",
                        }}
                      >
                        <div className="font-12 mt-2">
                          {hasPayment && (
                        <div className="row g-2 m-0">
                          {parseAmount(documentConfig?.cashPaid) > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>{documentConfig.cashPaid}</b> Paid via Cash
                              </span>
                            </div>
                          )}
                            {parseAmount(documentConfig?.cardPaid) > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>{documentConfig.cardPaid}</b> Paid via Card
                              </span>
                            </div>
                          )}
                          {parseAmount(documentConfig?.upiPaid) > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>{documentConfig.upiPaid}</b> Paid via UPI
                              </span>
                            </div>
                          )}
                        
                          {parseAmount(documentConfig?.onlinePaid) > 0 && (
                            <div className="col-6 font-12 ">
                              <span>
                                <b>{documentConfig.onlinePaid}</b> Paid via Online
                              </span>
                            </div>
                          )}
                        </div>
                         )}
                        </div>
                      </td>

                      {isInterState ? (
                        <>
                          <td
                            colSpan="2"
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                              verticalAlign: "top",
                            }}
                          >
                            IGST
                          </td>
                          <td
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                            }}
                          >
                            {documentConfig?.igstAmount || 0}
                          </td>
                        </>
                      ) : (
                        <>
                          <td
                            colSpan="2"
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                            }}
                          >
                            CGST
                          </td>
                          <td
                            style={{
                              border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                              textAlign: "right",
                              padding: "5px",
                            }}
                          >
                            {documentConfig?.cgstAmount || 0}
                          </td>
                        </>
                      )}
                    </tr>

                    {!isInterState && (
                      <tr className="font-10">
                        <td
                          colSpan="2"
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            padding: "5px",
                          }}
                        >
                          SGST
                        </td>
                        <td
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            padding: "5px",
                          }}
                        >
                          {documentConfig?.sgstAmount || 0}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>                
                <div className="row mt-2">
                  <div className="col-7 d-flex ">
                    <div className="">
            {documentConfig?.bankDetails && (
                          <>
                              <strong>Bank Details:</strong>
                                {documentConfig?.bankDetails
                                    ?.accountNo &&(
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Account #:
                                </span>
                                {
                                  documentConfig?.bankDetails
                                    ?.accountNo
                                }
                              </div>
                              )}
                               {documentConfig?.bankDetails
                                    ?.ifsc &&(
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>IFSC:</span>
                                {
                                  documentConfig?.bankDetails
                                    ?.ifsc
                                }
                              </div>
                              )}
                                {documentConfig?.bankDetails
                                    ?.beneficiaryName &&(
                               <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Name:</span>
                                {
                                  documentConfig?.bankDetails
                                    ?.beneficiaryName
                                }
                              </div>
                              )}
                                {documentConfig?.bankDetails
                                    ?.bankName &&(
                               <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Bank Name:</span>
                                {
                                 documentConfig?.bankDetails
                                    ?.bankName
                                }
                              </div>
                              )}
                              {documentConfig?.bankDetails
                                    ?.branch &&(
                               <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Branch Name:</span>
                                {
                                 documentConfig?.bankDetails
                                    ?.branch
                                }
                              </div>
                              )}
                              {documentConfig?.upiId &&(
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>UPI ID:</span>
                                {documentConfig?.upiId}
                              </div>
                              )}{" "}
                            </>
                       )}
                       </div>
                       {qrCodeDataUrl && (
                       <div className="col-3 ms-3 d-flex align-items-center justify-content-end">
                         <img
                           src={qrCodeDataUrl}
                          alt="QR"
                         style={{width: "100px",height: "100px",}}
                         />
                      </div>
                    )}   
                  </div>
                  <div className="col-5 ">
                    <div className="font-12 mt-2  text-end">
                          {storeSettings?.invoiceSettings?.signature ? (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              alt="Signature"
                              width="120"
                              style={{ marginBottom: "-5px" }}
                            />
                          ) : (
                            <div className="pt-4"></div>
                          )}
                          <div style={{ paddingTop: "5px" }}>
                            Authorised Signature
                          </div>
                        </div>
                  </div>
                </div>
 {documentConfig?.remarks ? (
                      <>
                      <div className="font-12 mt-2 fw-600 text-black mb-1">
                       Remark
                      </div>
                    
                      <div
                      className="font-12 mb-2 p-0"
                  style={{                
                whiteSpace: "pre-wrap",
               wordBreak: "break-word",
              }}
               >
              {capitalizeFirstLetter(documentConfig?.remarks)}
               </div>
              </>
                ) : null} 
                {storeSettings?.negobillBranding && (
                  <>
                    <div className="mt-5 font-12 d-flex justify-content-left ">
                      <span>
                        Powered By <span className="fw-bold">NegoBill.com</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : showBillDetails? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in p-2">
                <div style={{ display: "flex", paddingBottom: "10px" }}>
                  <div style={{ flex: "1" }}>
                    <div>
                      {
                        documentConfig?.image && (
                        <img
                          src={
                            documentConfig?.image
                          }
                          className="img-rounded logo"
                          height="40"
                          alt="Logo"
                        />
                      )}
                    </div>

                    <div className="font-12">
                      {documentConfig?.fromData?.addressline1}
                    </div>
                    <div className="font-12">
                      {documentConfig?.fromData?.addressline2}{" "}
                      {documentConfig?.fromData?.pincode || ""}
                    </div>
                    <div className="font-12">
                      <span className="fw-bold">Ph:</span>{" "}
                       {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )}{" "}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "center" }}>
                    <div className="font-14" style={{ color: "#555" }}>
                      {documentConfig?.title}
                    </div>
                    <div
                      className="font-15"
                      style={{
                        marginTop: "5px",
                        fontWeight: "800",
                        textTransform: "uppercase",
                        lineHeight: "1",
                      }}
                    >
                      {documentConfig?.fromData?.title || ""}
                    </div>
                  </div>

                  <div style={{ flex: "1", textAlign: "right" }}>
                    <div
                      style={{ display: "inline-block", textAlign: "right" }}
                    >
                      <div
                        className=" font-10"
                        style={{
                          borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          marginBottom: "4px",
                        }}
                      >{documentConfig?.numberLabel}
                        <span className="fw-bold"> {documentConfig?.number}</span>
                      </div>
                      <div className="d-flex justify-content-end font-10">
                        {/* <span>DATE: {saleTime?.date}</span> */}
                        <span> DATE: {documentConfig?.Date?.date}</span>
                        <span className="ms-3">TIME:{documentConfig?.Date?.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {documentConfig?.toData && (
                  <div
                    className="d-flex "
                    style={{
                      border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                    }}
                  >
                    <div
                      style={{
                        flex: "1",
                        padding: "5px",
                        borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                      }}
                    >
                      <span className="fw-bold font-12">
                        Customer Details :
                      </span>
                      <div className="font-12 ">{documentConfig?.toData?.name}</div>
                      <div className="font-12">
                        {" "}
                        {documentConfig?.toData?.addressline1 &&
                          `${documentConfig?.toData?.addressline1}, `}
                        {documentConfig?.toData?.addressline2 &&
                          `${documentConfig?.toData?.addressline2}, `}
                        {documentConfig?.toData?.district &&
                          `${documentConfig?.toData?.district}, `}
                        {documentConfig?.toData?.pincode &&
                          ` - ${documentConfig?.toData?.pincode}`}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "239px",
                        padding: "5px",
                        WebkitFontSmoothing: "antialiased",
                        textRendering: "optimizeLegibility",
                      }}
                    >
                      <div className="font-12">
                        <span className="fw-bold">Ph:</span>{" "}
                        {documentConfig?.toData?.phone || ""}
                      </div>
                       {documentConfig?.toData?.gstnumber &&(
                       <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                GSTIN:
                                              </span>{" "}
                                              {documentConfig?.toData?.gstnumber ||
                                                ""}
                                            </div> 
                       )}
                                             {documentConfig?.toData?.pannumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                PAN:
                                              </span>{" "}
                                              {documentConfig?.toData?.pannumber ||
                                                ""}
                                            </div>
                                            )}
                                            {documentConfig?.toData?.fssai &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                FASSI:
                                              </span>{" "}
                                              {documentConfig?.toData?.fssai ||
                                                ""}
                                            </div>
                                            )}
                                              {documentConfig?.toData?.cinnumber &&(
                                            <div className=" font-12">
                                              {" "}
                                              <span className="fw-bold">
                                                CIN:
                                              </span>{" "}
                                              {documentConfig?.toData?.cinnumber ||
                                                ""}
                                            </div>
                                              )}
                                            {documentConfig?.toData?.referenceid &&(
                                              <div className=" font-12">
                                              {" "}
                                              <span className="">
                                                Reference No:
                                              </span>{" "}
                                              {documentConfig?.toData?.referenceid ||
                                                ""}
                                            </div>
                                              )}
                    </div>
                  </div>
                )}
                <table
                  width="100%"
                  className="mt-0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr className=" fw-bold">
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "40px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "40px" }}
                      >
                        {" "}
                        #
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        // style={{ border: "1px solid ", padding: "5px", textAlign: "left" }}
                      >
                        ITEM
                      </th>

                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "80px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ", padding: "5px", width: "80px" }}
                      >
                        RATE
                      </th>
                      <th
                        className="force-style py-1 font-10"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        //  style={{ border: "1px solid ",  padding: "5px", width: "60px" }}
                      >
                        QTY
                      </th>

                      <th
                        // style={{ border: "1px solid ", padding: "5px", width: "100px" }}
                        className="force-style py-1 font-10"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        AMOUNT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentConfig?.items?.map((item, index) => (
                      <tr
                        key={index}
                        className="font-13"
                        style={{ height: "30px" }}
                      >
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,

                            textAlign: "center",
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            padding: "0 5px",
                          }}
                        >
                          {item?.title}
                        </td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          {item?.baseprice}
                        </td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "center",
                          }}
                        >
                          {item?.quantity}
                          {(() => {
                            const unitCode = item?.isSecondaryUnit
                              ? item?.unitConfig?.secondaryUnitCode
                              : item?.unitConfig?.primaryUnitCode;
                            return unitCode || "";
                          })()}
                        </td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            paddingRight: "5px",
                          }}
                        >
                          {item?.lineGrandTotal}
                        </td>
                      </tr>
                    ))}

                    {[
                      ...Array(
                        Math.max(0, 5 - (documentConfig?.items?.length || 0)),
                      ),
                    ].map((_, i) => (
                      <tr key={`empty-${i}`} style={{ height: "50px" }}>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            borderLeft: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>

                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                        <td
                          style={{
                            borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          }}
                        ></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Row 1: In Words & Total */}
                    <tr className="font-10">
                      <td
                        colSpan="2"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "left",
                          padding: "5px",
                        }}
                      >
                        <span className="fw-bold">In Words:</span>{" "}
                        {documentConfig?.amountInWords}
                      </td>
                      <td
                        colSpan="2"
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        TOTAL
                      </td>
                      <td
                        style={{
                          border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                          textAlign: "right",
                          padding: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        {documentConfig?.grandTotal}
                      </td>
                    </tr>
                    {/* <tr className="font-10">
                      <td
                        colSpan="4"
                        style={{
                          textAlign: "left",
                          padding: "5px",
                          verticalAlign: "top",
                        }}
                      >
                         {optData?.isPayment && (
                        <div className="row g-2 m-0">
                          {globaldocumentConfig?.cashPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{globalData.cashPaid}</b> Paid via Cash
                              </span>
                            </div>
                          )}
                          {globaldocumentConfig?.upiPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{globalData.upiPaid}</b> Paid via UPI
                              </span>
                            </div>
                          )}
                          {globaldocumentConfig?.cardPaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{globalData.cardPaid}</b> Paid via Card
                              </span>
                            </div>
                          )}
                          {globalData?.onlinePaid > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{globalData.onlinePaid}</b> Paid via Online
                              </span>
                            </div>
                          )}
                        </div>
                         )}
                      </td>
                      <td
                        colSpan="2"
                        className="p-0"
                        style={{
                          textAlign: "left",
                          verticalAlign: "bottom",
                        }}
                      >
                        <div className="font-12 mt-0 border p-2  ">
                          {storeSettings?.invoiceSettings?.signature ? (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              alt="Signature"
                              width="120"
                              style={{ marginBottom: "-5px" }}
                            />
                          ) : (
                            <div className="pt-4"></div>
                          )}
                          <div style={{ paddingTop: "5px" }}>
                            Authorised Signature
                          </div>
                        </div>
                      </td>
                    </tr> */}
                  </tfoot>
                </table>
                 <div className="row mt-2">
                  <div className="col-7 d-flex ">
                    <div className="">
            {documentConfig?.bankDetails &&  (
                          <>
                              <strong>Bank Details:</strong>
                                {documentConfig?.bankDetails
                                    ?.accountNo &&(
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Account #:
                                </span>
                                {
                                  documentConfig?.bankDetails
                                    ?.accountNo
                                }
                              </div>
                              )}
                               {documentConfig?.bankDetails
                                    ?.ifsc &&(
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>IFSC:</span>
                                {
                                  documentConfig?.bankDetails
                                    ?.ifsc
                                }
                              </div>
                              )}
                                {documentConfig?.bankDetails
                                    ?.beneficiaryName &&(
                               <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Name:</span>
                                {
                                  documentConfig?.bankDetails
                                    ?.beneficiaryName
                                }
                              </div>
                              )}
                                {documentConfig?.bankDetails
                                    ?.bankName &&(
                               <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Bank Name:</span>
                                {
                                 documentConfig?.bankDetails
                                    ?.bankName
                                }
                              </div>
                              )}
                              {documentConfig?.bankDetails
                                    ?.branch &&(
                               <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Branch Name:</span>
                                {
                                 documentConfig?.bankDetails
                                    ?.branch
                                }
                              </div>
                              )}
                              {documentConfig?.upiId &&(
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>UPI ID:</span>
                                {documentConfig?.upiId}
                              </div>
                              )}{" "}
                            </>
                       )}
                       </div>
                       {qrCodeDataUrl  && (
                       <div className="col-3 ms-3 d-flex align-items-center justify-content-end">
                         <img
                           src={qrCodeDataUrl}
                          alt="QR"
                         style={{width: "100px",height: "100px",}}
                         />
                      </div>
                    )}   
                  </div>
                  <div className="col-5">
                     {hasPayment && (
                        <div className="row g-2 m-0">
                          {parseAmount(documentConfig?.cashPaid) > 0 && (
                            <div className="col-6 font-12 ps-1 mb-1">
                              <span>
                                <b>{documentConfig.cashPaid}</b> Paid via Cash
                              </span>
                            </div>
                          )}
                           {parseAmount(documentConfig?.cardPaid) > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{documentConfig.cardPaid}</b> Paid via Card
                              </span>
                            </div>
                          )}
                          {parseAmount(documentConfig?.upiPaid) > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{documentConfig.upiPaid}</b> Paid via UPI
                              </span>
                            </div>
                          )}
                         
                          {parseAmount(documentConfig?.onlinePaid) > 0 && (
                            <div className="col-6 font-12 ps-1">
                              <span>
                                <b>{documentConfig.onlinePaid}</b> Paid via Online
                              </span>
                            </div>
                          )}
                        </div>
                         )}
                  </div>
                  </div>
                   {documentConfig?.remarks ? (
                      <>
                      <div className="font-12 mt-2 fw-600 text-black mb-1">
                       Remark
                      </div>
                    
                      <div
                      className="font-12 mb-2 p-0"
                  style={{               
                whiteSpace: "pre-wrap",
               wordBreak: "break-word",
              }}
               >
              {capitalizeFirstLetter(documentConfig?.remarks)}
               </div>
              </>
                ) : null} 
                  <div className="row mt-0">
                     <div className="col-12">
                    <div className="font-12 mt-2  text-end">
                          {storeSettings?.invoiceSettings?.signature ? (
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              alt="Signature"
                              width="120"
                              style={{ marginBottom: "-5px" }}
                            />
                          ) : (
                            <div className="pt-4"></div>
                          )}
                          <div style={{ paddingTop: "5px" }}>
                            Authorised Signature
                          </div>
                        </div>
                  </div>
                  </div>

                {storeSettings?.negobillBranding && (
                  <>
                    <div className="mt-5 font-12 d-flex justify-content-left ">
                      <span>
                        Powered By <span className="fw-bold">NegoBill.com</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
    </>
  );
};

export default CompactInvoice;
